import { clockMinutes } from '@ui/lib/time';

/** A day and two clock times into an entry: an end not after the start is work running past midnight, not a mistake. */

const DAY = 24 * 60;

/** Minutes worked between two clock times, counting a wrap around midnight as the next day. */
export const minutesWorked = (startTime: string, endTime: string): number => {
  const from = clockMinutes(startTime);
  const to = clockMinutes(endTime);
  if (from === undefined || to === undefined) return 0;
  return (to - from + DAY) % DAY;
};

/** Whether the work ran into the next day, which is what makes the end date differ from the day picked. */
export const runsPastMidnight = (startTime: string, endTime: string): boolean => {
  const from = clockMinutes(startTime);
  const to = clockMinutes(endTime);
  return from !== undefined && to !== undefined && to < from;
};

/** Two decimals, since the minute is the finest thing the fields can say. */
export const hoursFromMinutes = (minutes: number): number => Math.round((minutes / 60) * 100) / 100;

/** What is wrong with the two times, as a translation key: a pair that wraps midnight is a length, not a fault. */
export const timeErrorFor = (minutes: number): string | undefined =>
  minutes === 0 ? 'reportIncident.sameTime' : undefined;

/** "2h30", the way a length is said out loud. Whole hours drop the minutes. */
export const lengthLabel = (minutes: number): string => {
  const whole = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${whole}h` : `${whole}h${String(rest).padStart(2, '0')}`;
};

/** The instant a clock time falls on, read out of the day in UTC because a calendar day is stored as UTC midnight. */
export const instantOn = (day: Date, time: string): Date => {
  const minutes = clockMinutes(time) ?? 0;
  return new Date(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), Math.floor(minutes / 60), minutes % 60);
};

/** `instantOn` read backwards, in local time, so a start the form wrote comes back out unchanged. */
export const clockTimeOf = (at: Date): string =>
  `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;

/** A time on the same answer as `startTime`: one at or before the start belongs to the next day. */
export const instantAfterStart = (day: Date, startTime: string, time: string): Date =>
  new Date(instantOn(day, startTime).getTime() + minutesWorked(startTime, time) * 60000);

/** What is wrong with the moment it was fixed, as a translation key: only that it has not happened yet. */
export const resolvedErrorFor = (at: Date | null, now: Date = new Date()): string | undefined => {
  if (!at) return undefined;
  return at.getTime() > now.getTime() ? 'reportIncident.resolvedInFuture' : undefined;
};

/** Whether the work starts before the incident came in: a slipped day lands on a plausible hour, so it is worth catching. */
export const startsBeforeIncident = (startTime: string, day: Date, reportedAt: Date | null): boolean => {
  if (clockMinutes(startTime) === undefined || !reportedAt) return false;
  return instantOn(day, startTime).getTime() < reportedAt.getTime();
};
